import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Wallet, 
  TrendingUp, 
  Car, 
  Clock,
  Download,
  Calendar
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { toast } from '../../hooks/use-toast';
import DriverLayout from '../../components/driver/DriverLayout';

const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
  <Card className="p-6">
    <div className="flex items-center justify-between mb-2">
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      {trend && (
        <Badge className="bg-green-100 text-green-800">
          <TrendingUp className="h-3 w-3 mr-1" />
          {trend}
        </Badge>
      )}
    </div>
    <h3 className="text-2xl font-bold text-gray-900 mt-2">{value}</h3>
    <p className="text-sm text-gray-600">{title}</p>
    {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
  </Card>
);

const DriverAnalytics = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('week');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.get(`/drivers/analytics?period=${period}`);
      setAnalytics(response.data.analytics);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast({
        title: 'Error',
        description: 'Failed to load analytics data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const exportData = () => {
    if (!analytics) return;
    
    const csvData = [
      ['Date', 'Earnings', 'Rides', 'Commission', 'Net Earnings'],
      ...analytics.chartData.map(row => [
        row.date,
        row.earnings.toFixed(2),
        row.rides,
        row.commission.toFixed(2),
        row.netEarnings.toFixed(2)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `driver-analytics-${period}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast({
      title: 'Success',
      description: 'Analytics data exported successfully',
    });
  };

  if (loading) {
    return (
      <DriverLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading analytics...</p>
          </div>
        </div>
      </DriverLayout>
    );
  }

  const summary = analytics?.summary || {};
  const chartData = analytics?.chartData || [];
  const peakHours = analytics?.peakHours || [];

  return (
    <DriverLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Earnings Analytics</h1>
            <p className="text-gray-600 mt-1">Track your performance and earnings</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={exportData}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export Data
            </Button>
          </div>
        </div>

        {/* Period Selector */}
        <div className="flex gap-2">
          {['week', 'month', 'year'].map((p) => (
            <Button
              key={p}
              variant={period === p ? 'default' : 'outline'}
              onClick={() => setPeriod(p)}
              className={period === p ? 'bg-pink-600 hover:bg-pink-700' : ''}
            >
              <Calendar className="h-4 w-4 mr-2" />
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </Button>
          ))}
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Earnings"
            value={`K${summary.totalEarnings?.toFixed(2) || '0.00'}`}
            subtitle={`${summary.totalRides || 0} rides completed`}
            icon={Wallet}
            color="bg-green-500"
          />
          <StatCard
            title="Total Rides"
            value={summary.totalRides || 0}
            subtitle={`Avg K${summary.avgEarningsPerRide?.toFixed(2) || '0.00'} per ride`}
            icon={Car}
            color="bg-blue-500"
          />
          <StatCard
            title="Commission Paid"
            value={`K${summary.totalCommission?.toFixed(2) || '0.00'}`}
            subtitle={`20% of ride price`}
            icon={TrendingUp}
            color="bg-purple-500"
          />
          <StatCard
            title="Net Earnings"
            value={`K${summary.netEarnings?.toFixed(2) || '0.00'}`}
            subtitle="After commission"
            icon={Wallet}
            color="bg-pink-600"
          />
        </div>

        {/* Earnings Chart */}
        <Card className="p-6">
          <h2 className="text-xl font-bold mb-4">Earnings Trend</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip 
                formatter={(value) => `K${value.toFixed(2)}`}
                labelStyle={{ color: '#000' }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="earnings" 
                stroke="#10b981" 
                strokeWidth={2}
                name="Gross Earnings"
              />
              <Line 
                type="monotone" 
                dataKey="commission" 
                stroke="#ef4444" 
                strokeWidth={2}
                name="Commission"
              />
              <Line 
                type="monotone" 
                dataKey="netEarnings" 
                stroke="#ec4899" 
                strokeWidth={2}
                name="Net Earnings"
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Rides Chart */}
        <Card className="p-6">
          <h2 className="text-xl font-bold mb-4">Rides Completed</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar 
                dataKey="rides" 
                fill="#3b82f6" 
                name="Number of Rides"
              />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Peak Hours */}
        <Card className="p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-pink-600" />
            Peak Hours
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Your busiest times based on completed rides
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {peakHours.length > 0 ? (
              peakHours.map((peak, index) => (
                <div 
                  key={index}
                  className="bg-gradient-to-br from-pink-50 to-purple-50 p-4 rounded-lg border border-pink-200"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Rank #{index + 1}</p>
                      <p className="text-2xl font-bold text-pink-600">{peak.hour}</p>
                      <p className="text-sm text-gray-700 mt-1">
                        {peak.rides} {peak.rides === 1 ? 'ride' : 'rides'}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-pink-200 rounded-full flex items-center justify-center">
                      <Clock className="h-6 w-6 text-pink-600" />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-3 text-center py-8 text-gray-500">
                No peak hours data available yet. Complete more rides to see your busiest times!
              </div>
            )}
          </div>
        </Card>

        {/* Insights */}
        <Card className="p-6 bg-gradient-to-r from-pink-50 to-purple-50 border-pink-200">
          <h2 className="text-xl font-bold mb-3 text-pink-900">💡 Insights & Tips</h2>
          <div className="space-y-2 text-sm text-pink-800">
            {summary.totalRides > 0 ? (
              <>
                <p>• You're averaging <strong>K{summary.avgEarningsPerRide?.toFixed(2)}</strong> per ride</p>
                <p>• Commission is <strong>20% of ride price</strong> - keep your wallet topped up!</p>
                {peakHours.length > 0 && (
                  <p>• Your busiest time is around <strong>{peakHours[0].hour}</strong> - consider going online during these hours</p>
                )}
                <p>• Track your progress by switching between week, month, and year views</p>
              </>
            ) : (
              <p>• Complete your first ride to start seeing analytics and insights!</p>
            )}
          </div>
        </Card>
      </div>
    </DriverLayout>
  );
};

export default DriverAnalytics;
